import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import { Lightbulb, Send } from "lucide-react";

const STATUS_COLOR = {
  "New": "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Under Review": "border-amber-200 bg-amber-50 text-amber-700",
  "Implemented": "border-indigo-200 bg-indigo-50 text-indigo-700",
  "Rejected": "border-slate-200 bg-slate-50 text-slate-600",
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function Suggestions() {
  const [text, setText] = useState("");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/suggestions/mine").then(({ data }) => setList(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const t = text.trim();
    if (t.length < 4) { toast.error("Please add a bit more detail"); return; }
    setBusy(true);
    try {
      await api.post("/suggestions", { text: t });
      setText("");
      toast.success("Thanks! Your suggestion has been shared with the team.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — try again");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar role="agent" />
      <main className="flex-1 bg-[#FCFCFC] p-6 lg:p-10">
        <div className="max-w-3xl">
          <div className="mb-6">
            <div className="overline text-emerald-700">Share your ideas</div>
            <h1 className="font-editorial text-3xl mt-1 text-slate-900 flex items-center gap-3">
              <Lightbulb size={26} className="text-emerald-600" /> Suggestions
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Tell us what to build next — feature requests, improvements, bugs or any feedback. Our team reads every suggestion.
            </p>
          </div>

          <div className="bg-white border border-border rounded p-5 mb-8">
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Your suggestion</label>
            <textarea
              data-testid="sugg-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              placeholder="e.g. I'd love a quick WhatsApp share button on each quotation…"
              className="w-full border border-border rounded px-3 py-2.5 text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
              maxLength={4000}
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-[11px] text-slate-400">{text.length}/4000</span>
              <button
                data-testid="sugg-submit"
                onClick={submit}
                disabled={busy || text.trim().length < 4}
                className="btn-bronze inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Send size={14} /> {busy ? "Submitting…" : "Submit Suggestion"}
              </button>
            </div>
          </div>

          <div className="mb-3 text-xs uppercase tracking-widest text-slate-500">Your previous suggestions</div>
          {list.length === 0 ? (
            <div className="text-sm text-slate-400 bg-white border border-dashed border-border rounded px-4 py-6 text-center">
              You haven't submitted any suggestions yet.
            </div>
          ) : (
            <div className="space-y-3">
              {list.map((s) => (
                <div key={s.id} data-testid={`sugg-row-${s.id}`} className="bg-white border border-border rounded p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border ${STATUS_COLOR[s.status] || STATUS_COLOR["New"]}`}>{s.status}</span>
                    <span className="text-[11px] text-slate-400">{fmt(s.created_at)}</span>
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{s.text}</div>
                  {s.internal_note && (
                    <div className="mt-3 pt-3 border-t border-dashed border-border text-xs text-emerald-800 bg-emerald-50/50 -mx-4 -mb-4 px-4 py-2.5 rounded-b">
                      <b className="uppercase tracking-widest text-[10px]">Reply from team:</b> {s.internal_note}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
